// src/components/Navbar.js
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import logo from "../assets/images/ssspowerlifting_logo.png";

// Story sections live on the homepage and are reached by smooth-scrolling to an
// anchor; the content-heavy archives stay their own routes. Labels are kept
// stable ("Services", "About", "Apply/Contact") so existing tests/links hold.
const SECTION_LINKS = [
  { to: "/#about", label: "About", section: "about" },
  { to: "/#services", label: "Services", section: "services" },
];
const PAGE_LINKS = [
  { to: "/full-gallery", label: "Gallery" },
  { to: "/meet-recaps", label: "Meet Recaps" },
];
const CONTACT_LINK = { to: "/#contact", label: "Apply/Contact", section: "contact" };

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Highlight the nav item for whichever homepage section is currently in view.
const useActiveSection = (enabled) => {
  const [active, setActive] = useState("");

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === "undefined") {
      setActive("");
      return undefined;
    }
    const ids = [...SECTION_LINKS.map((l) => l.section), CONTACT_LINK.section];
    const els = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!els.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      // Collapse the root to a line at the viewport's vertical center, so the one
      // section crossing that line is "current" (deterministic, no overlap).
      { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled]);

  return active;
};

const Navbar = () => {
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const active = useActiveSection(onHome);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Solidify the bar once the user scrolls off the hero (subtle, no layout shift
  // — body padding stays at --nav-h so content never jumps).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu on route change and on Escape.
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  // On the homepage the logo is a scroll-to-top affordance (smooth); off the
  // homepage it stays a normal Link back to "/".
  const handleLogoClick = (e) => {
    closeMenu();
    if (!onHome) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  const renderSection = ({ to, label, section }) => (
    <li key={to}>
      <Link
        to={to}
        onClick={closeMenu}
        className={onHome && active === section ? "is-active" : undefined}
      >
        {label}
      </Link>
    </li>
  );

  return (
    <nav
      className={`navbar${scrolled ? " is-scrolled" : ""}${menuOpen ? " is-menu-open" : ""}`}
      aria-label="Primary"
    >
      <div className="navbar__inner">
        <div className="navbar__logo">
          <Link to="/" onClick={handleLogoClick}>
            <img src={logo} alt="SSS Powerlifting Logo" />
          </Link>
        </div>

        <ul className="navbar__links" id="primary-nav">
          {SECTION_LINKS.map(renderSection)}
          {PAGE_LINKS.map(({ to, label }) => (
            <li key={to}>
              <Link to={to} onClick={closeMenu}>
                {label}
              </Link>
            </li>
          ))}
          {renderSection(CONTACT_LINK)}
        </ul>

        <div className="navbar__actions">
          <button
            type="button"
            className="navbar__toggle"
            aria-expanded={menuOpen}
            aria-controls="primary-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="navbar__toggle-bar" />
            <span className="navbar__toggle-bar" />
            <span className="navbar__toggle-bar" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
